// =================================================================================================
// Template USAGE:
//  - Update AmChartSavingsData with widget settings
//  - Update AmChartYearlyActivitiesSettings with mapping of this settings in Settings Group
//  - Update ports and things information
//  - Register the widget to factory
// =================================================================================================

// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThingPort, WebThing } from '@API/WebThing';

import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import * as WidgetsThingsStructs from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';
import { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription, IWidgetSettingsProps } from '@UI/Widgets/WidgetStructs';

import { PortKeys, AmChartSavingsData } from './AmChartSavingsStructs';
import { am4ChartsTheme, amChartsData } from "../../Charts/amcharts/amChartsStructs";
import { SettingsGroup, SettingsType } from "@Primitive/SettingsListType";
import SettingsList from "@Primitive/SettingsList";
// ------------------------------------------------------------------------------------------------------
const AmChartSavings = React.lazy(() => import("./AmChartSavingsAsync"));
//const AmChartSavingsSettings = React.lazy(() => import("./AmChartSavingsAsync").then(module => ({ default: module.AmChartSavingsSettings })));
// =================================================================================================
// =================================================================================================
class AmChartSavingsSettings extends React.PureComponent<IWidgetSettingsProps<AmChartSavingsData>> {
    constructor(props: IWidgetSettingsProps<amChartsData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<amChartsData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.DropDown,
                        label: "Theme",
                        dataPath: "theme",
                        inSeperateLine: true,
                        items: [
                            { value: am4ChartsTheme.material, label: "Material" },
                            { value: am4ChartsTheme.amchartsdark, label: "amChartsDark" },
                            { value: am4ChartsTheme.amcharts, label: "amCharts" },
                            { value: am4ChartsTheme.dark, label: "Dark" },
                            { value: am4ChartsTheme.dataviz, label: "DataViz" },
                            { value: am4ChartsTheme.frozen, label: "Frozen" },
                            { value: am4ChartsTheme.kelly, label: "Kelly" },
                            { value: am4ChartsTheme.microchart, label: "Microchart" },
                            { value: am4ChartsTheme.moonrisekingdom, label: "Moon Rise Kingdom" },
                            { value: am4ChartsTheme.patterns, label: "Patterns" },
                            { value: am4ChartsTheme.spiritedaway, label: "Spirited Away" }
                        ]
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
        />;
    }
}
// =================================================================================================

export class AmChartSavingsWidget extends IWidgetThingsClass<AmChartSavingsData> {
    Render(key: string, width: number, height: number): React.ReactNode {

        return <WidgetsThingsStructs.LazyWidget >
            <AmChartSavings />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <AmChartSavingsSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />

    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: AmChartSavingsData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'PortKeyDescription',
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Data, data))
        );
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as AmChartSavingsData);

        // TOOD: Update thing description and widget icon

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): AmChartSavingsData {
        return {
            // TODO: Set the default values for the widget
            theme: am4ChartsTheme.amchartsdark
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return AmChartSavingsWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'amChart Saving widget',
            WidgetTitle: '',
            Key: 'AmChartSavingsWidget',
            Type: 'AmChartSavingsWidget',
            Data: AmChartSavingsWidget.GetDefaultWidgetDataStatic(),
            Description: 'AmChartSavingsWidget Description',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: AmChartSavingsData): AmChartSavingsData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}